import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Crown, Loader2, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import SiteFooter from '../components/SiteFooter';

const PremiumSuccess = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // checking | paid | expired | error
  const [attempts, setAttempts] = useState(0);
  const [paidSpread, setPaidSpread] = useState(new URLSearchParams(location.search).get('spread_type') || 'celtic-cross');

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    setAttempts(attempt + 1);
    const MAX_ATTEMPTS = 8;
    if (attempt >= MAX_ATTEMPTS) {
      setStatus('error');
      return;
    }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaidSpread(response.data.spread_type || paidSpread);
        setStatus('paid');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      // Still pending, poll again
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  };

  const spreadLabel = paidSpread === 'five-card' ? 'Five Card 5-card' : 'Celtic Cross 10-card';

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA] flex items-center justify-center px-6 relative" data-testid="premium-success-page">
      <div className="max-w-md w-full glass-effect rounded-lg p-12 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-heading font-medium mb-3">Confirming your payment...</h2>
            <p className="text-zinc-400 text-sm">Attempt {attempts}/8</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
            <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl font-heading font-medium mb-3">Reading Credit Ready</h2>
            <p className="text-zinc-400 mb-8">Your one-time payment added one {spreadLabel} reading credit.</p>
            <button
              onClick={() => navigate('/reading/new')}
              data-testid="start-celtic-cross-button"
              className="w-full py-3 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all font-medium flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Start Reading
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-3 py-2 text-zinc-400 hover:text-zinc-200 transition-colors text-sm"
            >
              Back to Dashboard
            </button>
          </>
        )}
        {(status === 'expired' || status === 'error') && (
          <>
            <XCircle className="w-16 h-16 text-zinc-500 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-2xl font-heading font-medium mb-3">
              {status === 'expired' ? 'Payment session expired' : 'Could not confirm payment'}
            </h2>
            <p className="text-zinc-400 mb-8">
              {status === 'expired' ? 'Please try again to upgrade.' : 'Please contact support if you were charged.'}
            </p>
            <button
              onClick={() => navigate('/premium')}
              data-testid="try-again-button"
              className="w-full py-3 border border-[#D4AF37] text-[#D4AF37] rounded-md hover:bg-[#D4AF37] hover:text-[#050505] transition-all"
            >
              Try Again
            </button>
          </>
        )}
      </div>
      <SiteFooter className="absolute bottom-0 left-0 right-0 border-white/5 py-4" />
    </div>
  );
};

export default PremiumSuccess;
